import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { Nullable } from '../../../core/models/Nullable';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';

@Component({
  selector: 'app-filters',
  template: `
    <form (ngSubmit)="search()">
      <div class="d-flex flex-column flex-md-row align-items-md-center gap-3">
        <ng-content select=".filter-create-button"></ng-content>

        <app-search-input [searchControl]="searchInput" [withSearchButton]="true" wrapperClass=""></app-search-input>

        <app-show-filter-button
          (showFiltersStatus)="changeShowFilters($event)"
          [howManyFiltersApplied]="(howManyAppliedFilters$ | async) || 0"
        ></app-show-filter-button>
        <app-clear-filter-button (clearClicked)="clearFilters()" *ngIf="hasFiltersApplied$ | async"></app-clear-filter-button>

        <div class="ms-auto">
          <ng-content select=".import-button"></ng-content>
        </div>
      </div>
      <div class="collapse" [ngbCollapse]="(showFilters$ | async) === false">
        <!-- the gap has to sit inside the collapsing element, and be padding rather than margin, so that
             it is part of the animated height instead of vanishing in one step at the end -->
        <div class="pt-3">
          <ng-content select=".filter-form"></ng-content>
        </div>
      </div>
    </form>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiltersComponent implements OnInit {
  @Input() filtersApplied$: Observable<object>;
  @Output() searchSubmitted = new EventEmitter<void>();
  @Input() relativeTo = this.route.children[0];
  showFilters$ = new BehaviorSubject<boolean>(false);
  searchInput = new UntypedFormControl(this.route.snapshot.queryParams.searchText || '');
  howManyAppliedFilters$: Observable<number>;
  hasFiltersApplied$: Observable<boolean>;

  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.howManyAppliedFilters$ = this.filtersApplied$.pipe(map((values) => Object.values(values).length));
    this.hasFiltersApplied$ = combineLatest([
      this.howManyAppliedFilters$,
      this.route.queryParamMap.pipe(map((params) => !!params.get('searchText'))),
    ]).pipe(map(([howMany, hasSearchText]) => howMany > 0 || hasSearchText));
  }

  // the filter form navigates too, so it may only run once this navigation has merged in the search text
  search(): void {
    this.router
      .navigate(['.'], {
        relativeTo: this.relativeTo,
        queryParams: { searchText: this.searchInput.value },
        queryParamsHandling: 'merge',
      })
      .then(() => this.searchSubmitted.emit());
  }

  changeShowFilters(showFilter: boolean): void {
    this.showFilters$.next(showFilter);
  }

  clearFilters(): void {
    this.searchInput.setValue('');
    const queryParams: {
      filters: Nullable<string>;
      startDate: Nullable<string>;
      endDate: Nullable<string>;
      searchText: Nullable<string>;
    } = {
      filters: null,
      startDate: null,
      endDate: null,
      searchText: null,
    };
    this.router.navigate(['.'], { relativeTo: this.relativeTo, queryParams, queryParamsHandling: 'merge' }).then(() => {});
  }
}
